import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import TodayIcon from '@mui/icons-material/Today';
import SettingsIcon from '@mui/icons-material/Settings';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import { businessConfigService } from '../services/businessConfigService';
import { calendarService } from '../services/calendarService';
import { ROUTES, getCalendarViewPath, getCalendarConfigPath } from '../constants/routes';
import { format, startOfWeek, endOfWeek } from 'date-fns';

/**
 * Calendar Overview Page - List all calendars with stats
 */
const CalendarOverviewPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [stats, setStats] = useState({});
  const [loadingStats, setLoadingStats] = useState({});

  // First, fetch just the calendar list
  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        setLoading(true);
        const calendarList = await businessConfigService.getBusinessCalendars(customerId);
        setCalendars(calendarList);
        
        // Initialize loading states for all calendars
        const initialLoadingStats = {};
        calendarList.forEach(calendar => {
          initialLoadingStats[calendar.name] = true;
        });
        setLoadingStats(initialLoadingStats);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCalendars();
    }
  }, [customerId]);

  // Then, lazy load stats for each calendar individually
  useEffect(() => {
    if (calendars.length === 0) return;

    const fetchStatsForCalendar = async (calendar) => {
      try {
        const today = new Date();
        const todayCustomersData = await calendarService.getDayEvents(customerId, today);
        const weekCustomersData = await calendarService.getWeekEvents(customerId, today);
        
        // Parse nested structure: {customerId: {business_name, calendars: {calendarName: {data}}}}
        let calendarTodayEvents = [];
        let calendarWeekEvents = [];
        
        Object.values(todayCustomersData).forEach(customerInfo => {
          const calendars = customerInfo.calendars || {};
          if (calendars[calendar.name]) {
            calendarTodayEvents = calendarTodayEvents.concat(calendars[calendar.name].calendar_events || []);
          }
        });
        
        Object.values(weekCustomersData).forEach(customerInfo => {
          const calendars = customerInfo.calendars || {};
          if (calendars[calendar.name]) {
            calendarWeekEvents = calendarWeekEvents.concat(calendars[calendar.name].calendar_events || []);
          }
        });

        // Update stats for this calendar
        setStats(prevStats => ({
          ...prevStats,
          [calendar.name]: {
            todayCount: calendarTodayEvents.length,
            weekCount: calendarWeekEvents.length,
          }
        }));
        
        // Mark this calendar's stats as loaded
        setLoadingStats(prev => ({
          ...prev,
          [calendar.name]: false
        }));
      } catch (err) {
        console.error(`Error fetching stats for ${calendar.name}:`, err);
        // Set zeros on error
        setStats(prevStats => ({
          ...prevStats,
          [calendar.name]: {
            todayCount: 0,
            weekCount: 0,
          }
        }));
        setLoadingStats(prev => ({
          ...prev,
          [calendar.name]: false
        }));
      }
    };

    // Fetch stats for each calendar with a small delay between them
    calendars.forEach((calendar, index) => {
      // Stagger the requests slightly to avoid overwhelming the API
      setTimeout(() => {
        fetchStatsForCalendar(calendar);
      }, index * 100);
    });
  }, [calendars, customerId]);

  const handleCalendarClick = (calendarName) => {
    navigate(getCalendarViewPath(customerId), {
      state: { selectedCalendar: calendarName },
    });
  };

  const handleViewAllCalendars = () => {
    navigate(getCalendarViewPath(customerId));
  };

  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  if (loading) {
    return <LoadingSpinner message="Loading calendars..." />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  if (calendars.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Calendars Found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You don&apos;t have any calendars configured yet.
          </Typography>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => navigate(getCalendarConfigPath(customerId))}
          >
            {t('dashboard.configureCalendars')}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Box>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
            >
              {t('dashboard.calendarManagement')}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              {t('dashboard.viewManageCalendars')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate(getCalendarConfigPath(customerId))}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('dashboard.configureCalendars')}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {calendars.map((calendar) => {
          const calendarStats = stats[calendar.name] || { todayCount: 0, weekCount: 0 };
          const isLoadingStats = loadingStats[calendar.name];
          
          return (
            <Grid item xs={12} sm={6} md={4} key={calendar.name}>
              <Card>
                <CardActionArea onClick={() => handleCalendarClick(calendar.name)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        component="div"
                        sx={{ 
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {calendar.name}
                      </Typography>
                      <CalendarMonthIcon color="primary" sx={{ flexShrink: 0 }} />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.today')}:
                      </Typography>
                      {isLoadingStats ? (
                        <Skeleton variant="rounded" width={40} height={24} sx={{ ml: 'auto', borderRadius: '16px' }} />
                      ) : (
                        <Chip 
                          label={calendarStats.todayCount} 
                          size="small" 
                          color="primary"
                          sx={{ ml: 'auto' }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.thisWeek')}:
                      </Typography>
                      {isLoadingStats ? (
                        <Skeleton variant="rounded" width={40} height={24} sx={{ ml: 'auto', borderRadius: '16px' }} />
                      ) : (
                        <Chip 
                          label={calendarStats.weekCount} 
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      )}
                    </Box>

                    {calendar.meeting_types && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.meetingTypes')}: {Object.keys(calendar.meeting_types).length}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default CalendarOverviewPage;
